import React, { useState } from 'react';
import { api } from '../api';
import './Modal.css';

export default function DeployModal({ onClose, onDeployed }) {
  const [domain, setDomain] = useState('');
  const [redisMemory, setRedisMemory] = useState('64mb');
  const [quotaGB, setQuotaGB] = useState(3);
  const [preview, setPreview] = useState('');
  const [previewMeta, setPreviewMeta] = useState(null);
  const [step, setStep] = useState('form'); // form | preview | deploying | done
  const [error, setError] = useState('');
  const [log, setLog] = useState('');

  async function handlePreview() {
    if (!domain.trim()) return setError('Domain is required');
    setError('');
    try {
      const { compose, meta } = await api.previewSite(domain.trim(), { redisMemory, quotaGB });
      setPreview(compose);
      setPreviewMeta(meta);
      setStep('preview');
    } catch (e) {
      setError(e.message);
    }
  }

  async function handleDeploy() {
    setStep('deploying');
    setError('');
    try {
      const result = await api.deploySite(domain.trim(), { redisMemory, quotaGB });
      setLog(`✅ Deployed ${domain}\n\nContainer names:\n- ${result.meta.safeName}-wordpress\n- ${result.meta.safeName}-db\n- ${result.meta.safeName}-redis\n\nDB: ${result.meta.dbName}\nUser: ${result.meta.dbUser}\nPass: ${result.meta.dbPass}`);
      setStep('done');
    } catch (e) {
      setError(e.message);
      setStep('preview');
    }
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h2>{step === 'done' ? '✅ Deployed!' : 'Deploy New Site'}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {step === 'form' && (
          <div className="modal-body">
            <div className="field">
              <label>Domain</label>
              <input
                placeholder="e.g. client.tofus.net or newsite.cz"
                value={domain}
                onChange={e => setDomain(e.target.value)}
                autoFocus
                onKeyDown={e => e.key === 'Enter' && handlePreview()}
              />
              <span className="field-hint">Used for container names, network name, and directory.</span>
            </div>
            <div className="field">
              <label>Redis Memory Limit</label>
              <select value={redisMemory} onChange={e => setRedisMemory(e.target.value)}>
                <option value="32mb">32mb</option>
                <option value="64mb">64mb (default)</option>
                <option value="128mb">128mb</option>
                <option value="256mb">256mb</option>
              </select>
            </div>
            <div className="field">
              <label>Disk Quota (GB)</label>
              <input
                type="number"
                min={1}
                value={quotaGB}
                onChange={e => setQuotaGB(parseInt(e.target.value, 10) || 1)}
              />
              <span className="field-hint">Hard cap — the client's whole directory (files + DB + cache) is mounted on its own filesystem this size.</span>
            </div>
            {error && <div className="modal-error">{error}</div>}
            <div className="modal-footer">
              <button className="btn-ghost" onClick={onClose}>Cancel</button>
              <button className="btn-primary" onClick={handlePreview}>Preview →</button>
            </div>
          </div>
        )}

        {step === 'preview' && (
          <div className="modal-body">
            <div className="preview-info">
              <p>Directory: <code>/home/leon/docker/clients/{domain}/</code></p>
              <p>Network: <code>{previewMeta?.netName}</code></p>
              <p>DB user: <code>{previewMeta?.dbUser}</code></p>
              <p>Quota: <code>{quotaGB}GB</code></p>
            </div>
            <label className="field-label">Generated docker-compose.yml</label>
            <textarea className="compose-preview" value={preview} readOnly rows={24} />
            {error && <div className="modal-error">{error}</div>}
            <div className="modal-footer">
              <button className="btn-ghost" onClick={() => setStep('form')}>← Back</button>
              <button className="btn-primary" onClick={handleDeploy}>🚀 Deploy</button>
            </div>
          </div>
        )}

        {step === 'deploying' && (
          <div className="modal-body center">
            <div className="spinner" style={{ width: 32, height: 32, borderWidth: 3 }} />
            <p style={{ marginTop: 16, color: 'var(--muted)' }}>Creating network, writing files, starting containers...</p>
          </div>
        )}

        {step === 'done' && (
          <div className="modal-body">
            <pre className="deploy-log">{log}</pre>
            <div className="modal-footer-note">
              ⚠ Add the proxy host in NPM manually: point <strong>{domain}</strong> → <strong>container:80</strong>
            </div>
            <div className="modal-footer">
              <button className="btn-primary" onClick={onDeployed}>Done</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
